/**
 * Sparkline Component
 *
 * Mini line chart for showing trends in metric cards with hover tooltips
 */

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Line, LineChart, ResponsiveContainer, Tooltip } from "recharts";
import { PriceDisplay } from "@/components/ui/price-display";
import { cn } from "@/lib/utils";
import { formatLocalizedDate } from "../lib/format-localized-date";

interface SparklineProps {
  data: { value: number; date?: string }[];
  trend: "up" | "down" | "neutral";
  format?: "number" | "currency" | "percentage" | "duration";
  className?: string;
}

function formatValue(value: number, format?: string): React.ReactNode {
  switch (format) {
    case "currency":
      return <PriceDisplay amount={value} />;
    case "percentage":
      return `${value.toFixed(1)}%`;
    case "duration":
      return `${Math.round(value)}m`;
    default:
      return new Intl.NumberFormat("en-US").format(value);
  }
}

export function Sparkline({ data, trend: _trend, format, className }: SparklineProps) {
  const { i18n } = useTranslation("reports");
  const strokeColor = "#3b82f6"; // Blue color for all sparklines

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    const dt = DateTime.fromISO(dateStr, { zone: "utc" });
    if (!dt.isValid) {
      // Try parsing as JS Date if ISO fails
      const jsDate = new Date(dateStr);
      if (!Number.isNaN(jsDate.getTime())) {
        return formatLocalizedDate(
          DateTime.fromJSDate(jsDate, { zone: "utc" }),
          "MMM d, yyyy",
          i18n.language
        );
      }
      return dateStr;
    }
    return formatLocalizedDate(dt, "MMM d, yyyy", i18n.language);
  };

  return (
    <ResponsiveContainer width="100%" height={40} className={cn("mt-2", className)}>
      <LineChart data={data}>
        <Tooltip
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const value = payload[0].value as number;
              const date = payload[0].payload.date;
              return (
                <div className="bg-popover border rounded-md px-2 py-1 shadow-md">
                  {date && (
                    <p className="text-[10px] text-muted-foreground mb-0.5">{formatDate(date)}</p>
                  )}
                  <p className="text-xs font-medium">{formatValue(value, format)}</p>
                </div>
              );
            }
            return null;
          }}
          cursor={{ stroke: strokeColor, strokeWidth: 1, strokeDasharray: "3 3" }}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke={strokeColor}
          strokeWidth={1.5}
          dot={false}
          isAnimationActive={true}
          animationDuration={800}
          animationEasing="ease-out"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}
